import React, { useEffect, useMemo, useState } from 'react';
import { activitiesAPI } from '../utils/api';

const emptyForm = {
  id: '',
  categoryKey: '',
  categoryLabel: '',
  title: '',
  sortOrder: 0,
};

const ActivityCatalogPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const categories = useMemo(() => {
    const map = new Map();
    templates.forEach((t) => {
      if (!map.has(t.category_key)) {
        map.set(t.category_key, t.category_label);
      }
    });
    return Array.from(map.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [templates]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await activitiesAPI.getTemplates();
      setTemplates(res.data?.templates || []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Erreur lors du chargement du catalogue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (template) => {
    setForm({
      id: template.id,
      categoryKey: template.category_key,
      categoryLabel: template.category_label,
      title: template.title,
      sortOrder: template.sort_order || 0,
    });
  };

  const resetForm = () => setForm(emptyForm);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        categoryKey: form.categoryKey.trim(),
        categoryLabel: form.categoryLabel.trim(),
        title: form.title.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      };

      if (form.id) {
        await activitiesAPI.updateTemplate(form.id, payload);
      } else {
        await activitiesAPI.createTemplate(payload);
      }
      resetForm();
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression du template ?')) return;
    try {
      await activitiesAPI.deleteTemplate(id);
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Catalogue des activités</h2>
          <p className="text-muted">{templates.length} templates actifs</p>
        </div>
      </div>

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>{form.id ? 'Modifier un template' : 'Ajouter un template'}</h3>
        {error && <div className="alert alert-error">⚠ {error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Catégorie (clé) *</label>
              <input
                className="form-input"
                value={form.categoryKey}
                onChange={(e) => setForm(f => ({ ...f, categoryKey: e.target.value }))}
                placeholder="ex: stimulation_cognitive"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Catégorie (label) *</label>
              <input
                className="form-input"
                value={form.categoryLabel}
                onChange={(e) => setForm(f => ({ ...f, categoryLabel: e.target.value }))}
                placeholder="ex: Stimulation cognitive et mémoire"
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Titre d’activité *</label>
              <input
                className="form-input"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="ex: Atelier mémoire"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Ordre</label>
              <input
                type="number"
                min={0}
                className="form-input"
                value={form.sortOrder}
                onChange={(e) => setForm(f => ({ ...f, sortOrder: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {form.id ? <button type="button" className="btn btn-secondary" onClick={resetForm}>Annuler</button> : null}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : form.id ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Templates existants</span>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{categories.length} catégories</span>
        </div>
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Clé</th>
                  <th>Titre</th>
                  <th>Ordre</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!templates.length ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>Aucun template</td></tr>
                ) : templates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.category_label}</td>
                    <td><code>{template.category_key}</code></td>
                    <td>{template.title}</td>
                    <td>{template.sort_order}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-outline" onClick={() => startEdit(template)}>Modifier</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(template.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityCatalogPage;

